import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAccount } from "./AppAccount";
import { AppClaudeState, AppClaudeToken } from "./AppClaudeToken";

// AppClaudeView - the markup of the owner's "Claude analysis" panel (`#claude`). See `AppClaudeToken`.
//
// ⚠ THE BOX IS `type="password"` WITH `autocomplete="off"`, AND IT IS ALWAYS PAINTED EMPTY. A repaint
// after a save or a refusal therefore clears it too - the token is never echoed back into the page.
// ⚠ EVERY SENTENCE IS INK (`inkwhy`), never the alarm orange: a malformed paste and a refused token
// are validation the owner acts on (law 1).

export class AppClaudeView {
    public static readonly BoxId: string = "wac-claude-token";
    public static readonly GoId: string = "wac-claude-save";

    /** The footer link. ⛔ "" unless `AppClaudeToken.ShowLink` - the owner's own browser only. */
    public static FootLink = (Show: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Show !== true) { return ""; }
            return "  &#183;  <a href=\"#" + AppClaudeToken.Route + "\">Claude analysis</a>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.FootLink");
            return "";
        }
    };

    /**
     * The panel. ⚠ The form appears only once the service has answered a status for this browser
     * (`Known`); before that it is "Checking..." or the service's own sentence, and for anybody who
     * is not the owner that sentence is all there is.
     */
    public static Panel = (State: AppClaudeState, WaysIn: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Body: string = "";
            if (State.Known === true) {
                Body = AppClaudeView.How() + AppClaudeView.Status(State) + AppClaudeView.InkWhy(State.Why)
                    + AppClaudeView.Form(State);
            } else if (State.Busy === true) {
                Body = "<p class=\"progress\" role=\"status\"><span class=\"spin\" aria-hidden=\"true\"></span>Checking&#8230;</p>";
            } else {
                // ⚠ `WaysIn` (markup, from the caller): a browser that is not signed in gets the two ways in
                // under the sentence - it used to be a sentence and nothing to press.
                Body = AppClaudeView.InkWhy(State.Why) + WaysIn;
            }
            return "<section class=\"signup account claude\"><div class=\"panel\">"
                + "<h1>Claude analysis</h1>"
                + "<p class=\"lede\">Analyse your conversations on your own Claude plan.</p>"
                + Body + "</div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.Panel");
            return "";
        }
    };

    /** How to get the token - three steps, in the owner's words. */
    private static How = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<ol class=\"plan how\">"
                + "<li>On your computer, in a terminal, run <code>claude setup-token</code>.</li>"
                + "<li>Approve it in the browser window that opens.</li>"
                + "<li>Copy the token it prints (it starts <code>sk-ant-oat01-</code>) and paste it"
                + " below.</li></ol>"
                + "<p class=\"hint\">WACommander checks the token with Claude before keeping it, and"
                + " keeps it in its key vault. It is never shown again, here or anywhere.</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.How");
            return "";
        }
    };

    private static Status = (State: AppClaudeState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Line: string = AppClaudeToken.StatusLine(State);
            if (Line.length === 0) { return ""; }
            return "<p class=\"tokenstate\" role=\"status\">" + kHTMLCore.EscapeHtml(Line) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.Status");
            return "";
        }
    };

    /** ⛔ The input is ALWAYS painted with no value. */
    private static Form = (State: AppClaudeState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Dis: string = "";
            let Label: string = "Save token";
            if (State.Busy === true) {
                Dis = " disabled";
                Label = "<span class=\"spin\" aria-hidden=\"true\"></span>Checking with Claude&#8230;";
            }
            let Word: string = "Your Claude Code token";
            if (State.Stored === true) { Word = "Replace it with a new token"; }
            return "<label class=\"flabel\" for=\"" + AppClaudeView.BoxId + "\">" + Word + "</label>"
                + "<div class=\"fieldrow\"><input id=\"" + AppClaudeView.BoxId + "\" type=\"password\""
                + " autocomplete=\"off\" autocapitalize=\"off\" spellcheck=\"false\""
                + " data-wac-go=\"" + AppClaudeView.GoId + "\"" + Dis + ">"
                + "<button type=\"button\" class=\"btn primary\" id=\"" + AppClaudeView.GoId + "\"" + Dis + ">"
                + Label + "</button></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.Form");
            return "";
        }
    };

    /** A sentence in ink, or "". */
    private static InkWhy = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Say: string = AppAccount.Sentence(nz(Why));
            if (Say.length === 0) { return ""; }
            return "<p class=\"inkwhy\" role=\"status\">" + kHTMLCore.EscapeHtml(Say) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeView.InkWhy");
            return "";
        }
    };
}
